import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import React from 'react'
import { ChargingSchedule } from 'src/Libs/flow/installation/forms'
import { formatIsoToHours } from 'src/Libs/flow/installation/utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

interface ChargingCalendarProps {
  schedule: ChargingSchedule
  includeTime: boolean
  onClickEditCalendar: () => void
}

/** The charging calendar component of user-level settings charging priority. */
export const ChargingCalendar: React.FC<ChargingCalendarProps> = ({ schedule, includeTime, onClickEditCalendar }) => {
  const { t } = useCustomTranslation()

  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const scheduleWithTime = includeTime ? schedule : schedule.map(entry => ({ ...entry, departureTime: '' }))

  const { monday, tuesday, wednesday, thursday, friday, saturday, sunday } = scheduleToWeekdayValues(scheduleWithTime)

  return (
    <Stack gap={2} width="100%">
      <Box>
        <Typography variant="body1" component={'h3'} mb={2} textAlign={'left'}>
          {t('user-settings.charging-priority-page.calendar.title')}
        </Typography>

        <Table sx={{ p: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ p: 0, border: 0 }}>
                <Typography variant="caption" fontWeight={'bold'}>
                  {t('user-settings.charging-priority-page.calendar.weekday')}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ p: 0, border: 0 }}>
                <Typography variant="caption" fontWeight={'bold'}>
                  {t('user-settings.charging-priority-page.calendar.minimal-energy')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <CalendarTableRow label={t('common.monday')} weekdayValue={monday} />
            <CalendarTableRow label={t('common.tuesday')} weekdayValue={tuesday} />
            <CalendarTableRow label={t('common.wednesday')} weekdayValue={wednesday} />
            <CalendarTableRow label={t('common.thursday')} weekdayValue={thursday} />
            <CalendarTableRow label={t('common.friday')} weekdayValue={friday} />
            <CalendarTableRow label={t('common.saturday')} weekdayValue={saturday} />
            <CalendarTableRow label={t('common.sunday')} weekdayValue={sunday} />
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button variant="contained" sx={{ minWidth: { xs: '100%', sm: 'auto' } }} disabled={!userCanEdit} onClick={onClickEditCalendar}>
          {t('user-settings.charging-priority-page.calendar.edit')}
        </Button>
      </Box>
    </Stack>
  )
}

type WeekdayValue = {
  /** ISO date */
  time: string
  energyKWh: number
} | null

const CalendarTableRow: React.FC<{ label: string; weekdayValue: WeekdayValue }> = ({ label, weekdayValue }) => {
  if (!weekdayValue) return null

  const { time, energyKWh } = weekdayValue

  const timePart = time ? `${formatIsoToHours(time)} - ` : ''

  return (
    <TableRow>
      <TableCell sx={{ p: 0, border: 0 }}>
        <Typography variant="caption">{label}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ p: 0, border: 0 }}>
        <Typography variant="caption" sx={{ color: theme => (energyKWh ? undefined : theme.palette.grey[200]) }}>
          {`${timePart}${energyKWh} kWh`}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

type WeekdayValues = {
  monday: WeekdayValue
  tuesday: WeekdayValue
  wednesday: WeekdayValue
  thursday: WeekdayValue
  friday: WeekdayValue
  saturday: WeekdayValue
  sunday: WeekdayValue
}

function scheduleToWeekdayValues(schedule: ChargingSchedule): WeekdayValues {
  return {
    monday: getValueForDay(schedule, 'monday'),
    tuesday: getValueForDay(schedule, 'tuesday'),
    wednesday: getValueForDay(schedule, 'wednesday'),
    thursday: getValueForDay(schedule, 'thursday'),
    friday: getValueForDay(schedule, 'friday'),
    saturday: getValueForDay(schedule, 'saturday'),
    sunday: getValueForDay(schedule, 'sunday'),
  }
}

function getValueForDay(schedule: ChargingSchedule, day: keyof ChargingSchedule[number]['weekDays']): WeekdayValue {
  /*
   *  Get the first schedule entry where the weekday value is true.
   *  This works because only one schedule entry has a value of true for a specific day.
   */
  const value = schedule.find(entry => !!entry.weekDays[day])

  if (!value) return null

  return { time: value.departureTime, energyKWh: value.energyWanted || 0 }
}
